import { useCallback, useMemo } from "react";
import { useUser } from "../../app/providers/userContext";

const permissionEntries = (access) => {
  if (Array.isArray(access)) return access;
  const candidates =
    access?.grants ??
    access?.effectiveGrants ??
    access?.effectivePermissions ??
    access?.permissions ??
    access?.data?.grants ??
    access?.data?.permissions ??
    [];
  return Array.isArray(candidates) ? candidates : [];
};

export const usePlatformPermissions = () => {
  const { user, platformAccess, platformAccessError, platformAccessLoading } = useUser();
  const isSuperAdmin = user?.roles?.includes("SUPER_ADMIN") || false;
  const grants = useMemo(
    () => permissionEntries(platformAccess).map((entry) => {
      if (typeof entry === "string") {
        return { permissionName: entry, globalScope: true, schoolIds: [] };
      }
      const scopedSchools = entry?.schoolIds ?? entry?.assignedSchoolIds ?? entry?.schools ?? [];
      return {
        permissionName: entry?.permissionName ?? entry?.name ?? entry?.permission?.name,
        globalScope: entry?.globalScope === true || String(entry?.scope || "").toUpperCase() === "GLOBAL",
        schoolIds: (Array.isArray(scopedSchools) ? scopedSchools : [scopedSchools])
          .map((school) => Number(typeof school === "object" ? school.id ?? school.schoolId : school))
          .filter(Number.isFinite),
      };
    }),
    [platformAccess],
  );

  const can = useCallback((permission, schoolId) => {
    if (isSuperAdmin) return true;
    return grants.some((grant) =>
      grant.permissionName === permission &&
      (grant.globalScope || (schoolId != null && grant.schoolIds.includes(Number(schoolId))))
    );
  }, [grants, isSuperAdmin]);

  const isPermissionGranted = useCallback((permission) =>
    Boolean(permission) && (isSuperAdmin || grants.some((grant) => grant.permissionName === permission)),
  [grants, isSuperAdmin]);
  const assignedSchoolIds = useMemo(
    () => [...new Set(grants.flatMap((grant) => grant.schoolIds))],
    [grants],
  );
  const hasGlobalAccess = useCallback((permission) =>
    isSuperAdmin || grants.some((grant) => grant.permissionName === permission && grant.globalScope),
  [grants, isSuperAdmin]);

  return {
    isSuperAdmin,
    grants,
    can,
    isPermissionGranted,
    hasGlobalAccess,
    assignedSchoolIds,
    platformAccessError,
    platformAccessLoading,
  };
};
